import React, { createContext, useReducer } from 'react';

/**
 * 建立 Context，預設值裡放一個空的 state 與空的 dispatch
 */
export const BellNotificationContext = createContext({
  state: { bellNotifications: [] },
  dispatch: () => {
    console.warn('未初始化 BellNotificationContext Provider');
  },
});

/**
 * 初始狀態：notifications 用來存「所有公司合併後」的通知列表
 */
const initialState = {
  bellNotifications: [],
};


/**
 * Reducer 處理不同類型的通知
 */
function bellNotificationReducer(state, action) {
  // console.log('Reducer received action:', action);
  switch (action.type) {
    case 'INITIAL_BELLNOTIFICATIONS': {
      return {
        ...state,
        bellNotifications: action.payload
      };
    }

    case 'UPDATE_BELLNOTIFICATIONS': {
      // 1. 先把 payload 裡的資料暫存到一個 Map，key = company.customer, value = notifications
      //    用來和舊的做對應
      const newNotificationsMap = new Map();
      action.payload.forEach((company) => {
        newNotificationsMap.set(company.customer, company.notifications);
      });

      // 2. 建立一個陣列，最後要用它組合新的 bellNotifications
      let updatedBellNotifications = [];

      // 3. 先遍歷舊的 state 裡的 bellNotifications
      //    若該公司有在 newNotificationsMap，就和舊的合併再去重、排序；
      //    否則就維持舊的
      state.bellNotifications.forEach((oldCompany) => {
        const { customer, notifications, updatedAt } = oldCompany; // 取得舊的資料
        const newNotifications = newNotificationsMap.get(customer);// 取得新進來的資料

        if (newNotifications) {

          // 依照通知的 _id 做去重，以 **最新的通知覆蓋舊的**
          const uniqueMap = new Map();
          [...notifications, ...newNotifications].forEach((notif) => {
            const existingNotif = uniqueMap.get(notif._id);
            if (!existingNotif || new Date(existingNotif.updatedAt) < new Date(notif.updatedAt)) {
              uniqueMap.set(notif._id, notif);
            }
          });

          //轉回陣列並按照 currentRegions.timestamp 降序排序
          const uniqueNotifications = Array.from(uniqueMap.values()).sort(
            (a, b) => new Date(b.currentRegions?.timestamp || 0) - new Date(a.currentRegions?.timestamp || 0)
          );

          // 3-4. 放進最終結果陣列
          updatedBellNotifications.push({
            customer,
            notifications: uniqueNotifications,
            updatedAt: updatedAt,
          });

          // 3-5. 合併完以後就可以把 newNotificationsMap 裡的資料刪掉，
          //      避免下一步再重複處理
          newNotificationsMap.delete(customer);
        } else {
          // 這家公司沒更新，就直接放舊的
          updatedBellNotifications.push(oldCompany);
        }
      });

      // 4. 若 payload 裡還有一些是「完全新的公司」(舊 state 裡找不到)，
      //    就直接把它們加入
      newNotificationsMap.forEach((notifications, customer) => {
        // 一樣先去重、再排序
        const uniqueMap = new Map();
        notifications.forEach((notif) => {
          uniqueMap.set(notif._id, notif);
        });
        const uniqueNotifications = Array.from(uniqueMap.values());

        uniqueNotifications.sort(
          (a, b) => new Date(b.currentRegions?.timestamp || 0) - new Date(a.currentRegions?.timestamp || 0)
        );

        updatedBellNotifications.push({
          customer,
          notifications: uniqueNotifications,
        });
      });

      // 5. 回傳新的 state
      return {
        ...state,
        bellNotifications: updatedBellNotifications,
      };
    }

    case 'MORE_BELLNOTIFICATIONS': {
      const existingMap = new Map();

      // 先把 state 裡的 bellNotifications 做成一個 Map，key=customer, value=notifications(舊的)
      state.bellNotifications.forEach(item => {
        existingMap.set(item.customer, item.notifications);
      });

      // 把 payload 裡的每個 item，用同樣的 key=customer 去合併
      action.payload.forEach(item => {
        const oldList = existingMap.get(item.customer) || [];
        const newList = [...oldList, ...item.notifications]; // **將新舊資料合併**

        // 依照 `_id` 去重，保留最先出現的通知
        const uniqueMap = new Map();
        newList.forEach((notif) => {
          if (!uniqueMap.has(notif._id)) {
            uniqueMap.set(notif._id, notif);
          }
        });

        existingMap.set(item.customer, Array.from(uniqueMap.values()));
      });

      // 再把 Map 轉回陣列
      const updatedBellNotifications = Array.from(existingMap.entries()).map(([customer, notifications]) => ({
        customer,
        notifications,
      }));

      return {
        ...state,
        bellNotifications: updatedBellNotifications,
      };
    }

    default:
      return state;
  }
}

/**
 * BellNotificationProvider：負責存放通知資料
 */
export const BellNotificationProvider = ({ children }) => {
  const [state, dispatch] = useReducer(bellNotificationReducer, initialState);

  return (
    <BellNotificationContext.Provider value={{ state, dispatch }}>
      {children}
    </BellNotificationContext.Provider>
  );
};

export default BellNotificationProvider;
